import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Send, CheckCircle, User, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { auth, db } from '@/lib/firebase';
import { collection, query, where, getDocs, addDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { contactRequestsApi } from '@/services/api/contact-requests.api';
import { mergeDemoBuyerPartners } from '@/data/demo-partners';

interface Partner {
  id: string;
  name: string;
  role: string;
  company?: string;
  type: 'internal' | 'external';
}

interface ContactSellerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  dealName?: string;
  language: string;
  onRequestSent?: () => void;
}

const TRANSLATIONS = {
  en: {
    title: 'Contact the Seller',
    description: 'Send a contact request to the seller/broker. Choose whether to include your partners.',
    profileOnly: 'Send with my profile only',
    profileOnlyDesc: 'The seller will receive your buyer profile information.',
    withPartners: 'Include partners in my request',
    withPartnersDesc: 'Add internal or external partners to strengthen your request.',
    selectPartners: 'Select partners to include:',
    internalPartners: 'Internal Partners',
    externalPartners: 'External Partners',
    noPartners: 'No partners found. Add partners from your dashboard.',
    send: 'Send Request',
    sending: 'Sending...',
    success: 'Request Sent',
    successDesc: 'The seller/broker will review your request and respond shortly.',
    error: 'Error',
    errorDesc: 'Failed to send contact request. Please try again.',
    selected: 'selected',
  },
  fr: {
    title: 'Contacter le Vendeur',
    description: 'Envoyez une demande de contact au vendeur/courtier. Choisissez d\'inclure vos partenaires ou non.',
    profileOnly: 'Envoyer avec mon profil seulement',
    profileOnlyDesc: 'Le vendeur recevra les informations de votre profil acheteur.',
    withPartners: 'Inclure des partenaires dans ma demande',
    withPartnersDesc: 'Ajoutez des partenaires internes ou externes pour renforcer votre demande.',
    selectPartners: 'Sélectionnez les partenaires à inclure :',
    internalPartners: 'Partenaires Internes',
    externalPartners: 'Partenaires Externes',
    noPartners: 'Aucun partenaire trouvé. Ajoutez des partenaires depuis votre tableau de bord.',
    send: 'Envoyer la demande',
    sending: 'Envoi...',
    success: 'Demande envoyée',
    successDesc: 'Le vendeur/courtier examinera votre demande et répondra sous peu.',
    error: 'Erreur',
    errorDesc: "Échec de l'envoi de la demande. Veuillez réessayer.",
    selected: 'sélectionné(s)',
  },
};

export function ContactSellerDialog({ open, onOpenChange, dealId, dealName, language, onRequestSent }: ContactSellerDialogProps) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [includePartners, setIncludePartners] = useState(false);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState<Set<string>>(new Set());
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const { toast } = useToast();
  const t = TRANSLATIONS[language as keyof typeof TRANSLATIONS] || TRANSLATIONS.en;

  // Load partners from Firestore when dialog opens
  useEffect(() => {
    if (!open || !auth.currentUser) return;

    const loadPartners = async () => {
      setLoadingPartners(true);
      try {
        const partnersRef = collection(db, 'buyerPartners');
        const q = query(partnersRef, where('buyerId', '==', auth.currentUser!.uid));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Partner[];
        setPartners(mergeDemoBuyerPartners(data));
      } catch (error) {
        console.error('Error loading partners:', error);
        setPartners(mergeDemoBuyerPartners([]));
      } finally {
        setLoadingPartners(false);
      }
    };

    loadPartners();
  }, [open]);

  const togglePartner = (partnerId: string) => {
    setSelectedPartnerIds(prev => {
      const next = new Set(prev);
      if (next.has(partnerId)) next.delete(partnerId);
      else next.add(partnerId);
      return next;
    });
  };

  const internalPartners = partners.filter(p => p.type === 'internal');
  const externalPartners = partners.filter(p => p.type === 'external');

  const handleSend = async () => {
  if (!auth.currentUser) return;
  setSending(true);
  try {
    const partnerIds = includePartners ? Array.from(selectedPartnerIds) : [];

    // Appel NestJS — gère notifications + email automatiquement
    await contactRequestsApi.create({
      dealId,
      message: `Contact request for ${dealName || dealId}`,
      partnerIds,
    });

    setSent(true);
    toast({ title: t.success, description: t.successDesc });
    onRequestSent?.();
    setTimeout(() => {
      onOpenChange(false);
      setSent(false);
      setIncludePartners(false);
      setSelectedPartnerIds(new Set());
    }, 2000);
  } catch (error) {
    console.error('Error sending contact request:', error);
    toast({
      title: t.error,
      description: t.errorDesc,
      variant: 'destructive',
    });
  } finally {
    setSending(false);
  }
};

  const handleClose = (v: boolean) => {
    onOpenChange(v);
    if (!v) {
      setSent(false);
      setIncludePartners(false);
      setSelectedPartnerIds(new Set());
    }
  };

  const renderPartnerList = (list: Partner[], label: string) => {
    if (list.length === 0) return null;
    return (
      <div className="space-y-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</p>
        {list.map(partner => (
          <label
            key={partner.id}
            className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
          >
            <Checkbox
              checked={selectedPartnerIds.has(partner.id)}
              onCheckedChange={() => togglePartner(partner.id)}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{partner.name}</p>
              <p className="text-xs text-muted-foreground truncate">
                {partner.role}{partner.company ? ` • ${partner.company}` : ''}
              </p>
            </div>
          </label>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-primary" />
            {t.title}
          </DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="flex flex-col items-center py-8 gap-3">
            <CheckCircle className="h-12 w-12 text-green-500" />
            <p className="text-lg font-semibold text-green-700">{t.success}</p>
            <p className="text-sm text-muted-foreground text-center">{t.successDesc}</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setIncludePartners(false)}
                className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                  !includePartners
                    ? 'border-[#b8924f] bg-[#b8924f]/5'
                    : 'border-border hover:border-muted-foreground/30'
                }`}
              >
                <div className="flex items-center gap-3">
                  <User className={`h-5 w-5 ${!includePartners ? 'text-[#b8924f]' : 'text-muted-foreground'}`} />
                  <div>
                    <p className="font-medium text-sm">{t.profileOnly}</p>
                    <p className="text-xs text-muted-foreground">{t.profileOnlyDesc}</p>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setIncludePartners(true)}
                className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                  includePartners
                    ? 'border-[#b8924f] bg-[#b8924f]/5'
                    : 'border-border hover:border-muted-foreground/30'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Users className={`h-5 w-5 ${includePartners ? 'text-[#b8924f]' : 'text-muted-foreground'}`} />
                  <div>
                    <p className="font-medium text-sm">{t.withPartners}</p>
                    <p className="text-xs text-muted-foreground">{t.withPartnersDesc}</p>
                  </div>
                  {includePartners ? (
                    <ChevronUp className="h-4 w-4 ml-auto text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 ml-auto text-muted-foreground" />
                  )}
                </div>
              </button>

              {includePartners && (
                <div className="border rounded-lg p-3 space-y-3 max-h-60 overflow-y-auto">
                  {loadingPartners ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : partners.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-3">{t.noPartners}</p>
                  ) : (
                    <>
                      <p className="text-sm font-medium">{t.selectPartners}</p>
                      {renderPartnerList(internalPartners, t.internalPartners)}
                      {renderPartnerList(externalPartners, t.externalPartners)}
                      {selectedPartnerIds.size > 0 && (
                        <p className="text-xs text-[#b8924f] font-medium">
                          {selectedPartnerIds.size} {t.selected}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                onClick={handleSend}
                disabled={sending || (includePartners && selectedPartnerIds.size === 0)}
                className="w-full bg-gradient-to-r from-[#b8924f] to-[#d4af6a]"
              >
                {sending ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t.sending}</>
                ) : (
                  <><Send className="mr-2 h-4 w-4" />{t.send}</>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
